import React from 'react';
import WaterIntelCurrentPanel from './WaterIntelCurrentPanel';
import WaterIntelDepthPanel from './WaterIntelDepthPanel';

type Props = {
  read: any;
  loading: boolean;
  error?: string | null;
  isDarkMode: boolean;
  environment?: any;
  target?: { lat: number; lng: number } | null;
  onClose: () => void;
  onRefresh: () => void;
  onLogCatch?: (target: { lat: number; lng: number }) => void;
  onSearchFishIQ?: (target: { lat: number; lng: number }) => void;
  maxHeightPx?: number;
};

function formatCoordinate(value: number, positive: string, negative: string) {
  const direction = value >= 0 ? positive : negative;
  return `${Math.abs(value).toFixed(4)}° ${direction}`;
}

export default function WaterIntelCard({
  read,
  loading,
  error,
  isDarkMode,
  environment,
  target,
  onClose,
  onRefresh,
  onLogCatch,
  onSearchFishIQ,
  maxHeightPx,
}: Props) {
  const current = read?.currents;
  const bathymetry = read?.bathymetry;
  const hasCoverage = !!current?.available || !!bathymetry?.available;
  const summary = String(read?.interpretation?.summary || '').trim();

  return (
    <div
      className="w-[min(360px,calc(100vw-24px))] max-h-[min(640px,calc(100dvh-190px))] overflow-y-auto overscroll-contain rounded-[22px] border p-3 shadow-2xl backdrop-blur-xl sm:p-3.5"
      style={{
        ...(Number.isFinite(maxHeightPx) ? { maxHeight: `${Math.max(300, Number(maxHeightPx))}px` } : null),
        background: isDarkMode ? 'rgba(7,15,28,0.97)' : 'rgba(255,250,243,0.97)',
        borderColor: isDarkMode ? 'rgba(124,199,255,0.16)' : 'rgba(91,67,49,0.12)',
        color: isDarkMode ? '#F2F6FC' : '#3D342D',
      }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div
            className="text-[8.5px] font-black uppercase tracking-[0.18em]"
            style={{ color: isDarkMode ? '#66D9E8' : '#24779B' }}
          >
            Water Intel
          </div>
          <div className="mt-0.5 text-[16px] font-black leading-tight sm:text-[17px]">
            What is this water doing?
          </div>

          {target ? (
            <div
              className="mt-1.5 inline-flex max-w-full items-center gap-1 rounded-full border px-2 py-0.5 text-[8.5px] font-extrabold tabular-nums"
              style={{
                background: isDarkMode ? 'rgba(102,217,232,0.05)' : 'rgba(234,247,255,0.66)',
                borderColor: isDarkMode ? 'rgba(102,217,232,0.12)' : 'rgba(86,174,220,0.18)',
                color: isDarkMode ? '#CDEFFF' : '#246C91',
              }}
              title={`${target.lat.toFixed(6)}, ${target.lng.toFixed(6)}`}
            >
              <span aria-hidden="true" className="text-[9px]">◎</span>
              <span className="truncate">
                {formatCoordinate(target.lat, 'N', 'S')} · {formatCoordinate(target.lng, 'E', 'W')}
              </span>
            </div>
          ) : null}
        </div>

        <button
          type="button"
          onClick={onClose}
          aria-label="Close Water Intel"
          className="grid h-8 w-8 shrink-0 place-items-center rounded-full border text-[18px] font-black active:scale-95"
          style={{
            background: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.72)',
            borderColor: isDarkMode ? 'rgba(255,255,255,0.10)' : 'rgba(91,67,49,0.10)',
          }}
        >
          ×
        </button>
      </div>

      {loading ? (
        <div className="grid min-h-[150px] place-items-center text-center">
          <div>
            <div className="mx-auto h-7 w-7 animate-spin rounded-full border-2 border-current border-r-transparent opacity-70" />
            <div className="mt-3 text-[10px] font-black uppercase tracking-[0.12em] opacity-60">
              Reading water…
            </div>
          </div>
        </div>
      ) : error ? (
        <div
          className="mt-3 rounded-[16px] border p-4 text-center"
          style={{ borderColor: 'rgba(239,68,68,0.20)' }}
        >
          <div className="text-[12px] font-bold">Water Intel is unavailable right now.</div>
          <button
            type="button"
            onClick={onRefresh}
            className="mt-3 rounded-full border px-3 py-1.5 text-[9px] font-black uppercase tracking-[0.08em]"
          >
            Try again
          </button>
        </div>
      ) : (
        <>
          <div className="mt-3 grid grid-cols-2 gap-2 sm:gap-2.5">
            <WaterIntelCurrentPanel current={current} isDarkMode={isDarkMode} />
            <WaterIntelDepthPanel bathymetry={bathymetry} isDarkMode={isDarkMode} />
          </div>

          {summary ? (
            <div
              className="relative mt-3 overflow-hidden rounded-[16px] border px-3.5 py-3"
              style={{
                background: isDarkMode
                  ? 'linear-gradient(135deg, rgba(102,217,232,0.085), rgba(124,199,255,0.035))'
                  : 'linear-gradient(135deg, rgba(234,247,255,0.94), rgba(255,255,255,0.72))',
                borderColor: isDarkMode ? 'rgba(102,217,232,0.16)' : 'rgba(86,174,220,0.22)',
              }}
            >
              <div
                aria-hidden="true"
                className="absolute bottom-0 left-0 top-0 w-[3px]"
                style={{ background: isDarkMode ? '#66D9E8' : '#4EA5E9' }}
              />
              <div
                className="text-[8px] font-black uppercase tracking-[0.16em]"
                style={{ color: isDarkMode ? '#8DE7F1' : '#24779B' }}
              >
                Fishing read
              </div>
              <div className="mt-1.5 text-[11px] font-bold leading-[1.48] opacity-90">
                {summary}
              </div>
            </div>
          ) : !hasCoverage ? (
            <div
              className="mt-3 rounded-[15px] border px-3 py-2.5 text-[10px] font-bold opacity-70"
              style={{
                borderColor: isDarkMode ? 'rgba(255,255,255,0.09)' : 'rgba(91,67,49,0.09)',
              }}
            >
              Coverage is limited here. Big Blue and FishIQ continue normally.
            </div>
          ) : null}

          {target && (onLogCatch || onSearchFishIQ) ? (
            <div className="mt-3 grid grid-cols-2 gap-2">
              {onLogCatch ? (
                <button
                  type="button"
                  onClick={() => onLogCatch(target)}
                  className="h-9 rounded-[12px] border px-3 text-[9.5px] font-black uppercase tracking-[0.10em] active:scale-[0.985]"
                  style={{
                    background: isDarkMode ? 'rgba(102,214,169,0.12)' : 'rgba(234,255,245,0.96)',
                    borderColor: isDarkMode ? 'rgba(102,214,169,0.18)' : 'rgba(172,235,204,1)',
                    color: isDarkMode ? '#DFFFF1' : '#256A54',
                  }}
                >
                  Log Catch
                </button>
              ) : null}

              {onSearchFishIQ ? (
                <button
                  type="button"
                  onClick={() => onSearchFishIQ(target)}
                  className="h-9 rounded-[12px] border px-3 text-[9.5px] font-black uppercase tracking-[0.10em] active:scale-[0.985]"
                  style={{
                    background: isDarkMode ? 'rgba(102,217,232,0.12)' : 'rgba(234,247,255,0.96)',
                    borderColor: isDarkMode ? 'rgba(102,217,232,0.22)' : 'rgba(86,174,220,0.28)',
                    color: isDarkMode ? '#DDF6FF' : '#22638A',
                  }}
                >
                  FishIQ
                </button>
              ) : null}
            </div>
          ) : null}

          <div className="mt-3 flex items-center justify-between gap-3 border-t pt-2.5"
            style={{ borderColor: isDarkMode ? 'rgba(255,255,255,0.07)' : 'rgba(91,67,49,0.08)' }}
          >
            <div className="text-[7.5px] font-bold leading-3 opacity-45">
              Fishing reference only · Not for navigation.
            </div>
            <button
              type="button"
              onClick={onRefresh}
              className="shrink-0 rounded-full border px-2.5 py-1 text-[8px] font-black uppercase tracking-[0.08em] active:scale-95"
            >
              Refresh
            </button>
          </div>
        </>
      )}
    </div>
  );
}
